import React, { useState, useEffect, useCallback, useContext } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { CiSearch } from "react-icons/ci";
import { MdPreview, MdPrint, MdClose } from "react-icons/md";
import Select from "react-select";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import PlaceBetServices from "../../services/PlaceBetServices";
import PlaceBetDownload from "./components/PlaceBetDownload";
import PlaceBetDetailYantraList, {
  PlaceBetPrintYantraGrid,
} from "./components/PlaceBetDetailYantraList";
import "../../styles/PlaceBetManagement.css";
import "../../styles/ReportManage.css";
import { AuthContext } from "../../context/AuthContext";
import TransactionHistoryServices from "../../services/TransactionHistoryServices";
import { selectCustomStyles } from "../../utils/utils";
import useActiveGames from "../../hooks/useActiveGames";

const FILTER_TYPE_OPTIONS = [
  { value: "", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last3", label: "Last 3 Days" },
  { value: "last7", label: "Last 7 Days" },
];

/** Remove YT_PLACEBET_ prefix for display/print e.g. YT_PLACEBET_1773031729812_4KKR → 1773031729812_4KKR */
const stripPlaceBetUidPrefix = (val) => {
  if (val == null || val === "") return "—";
  const s = String(val).trim();
  return s.replace(/^YT_PLACEBET_/i, "") || s || "—";
};

const PlaceBetManagement = () => {
  // Session filter options come from the running games. The fixed pair
  // here meant a game added later could never be filtered for.
  const { games: activeGames } = useActiveGames({ remember: false });
  const gameTypeOptions = [
    { value: "", label: "All Sessions" },
    ...activeGames.map((g) => ({ value: g.game_code, label: g.game_name })),
  ];

  const api = new PlaceBetServices();
  const txApi = new TransactionHistoryServices();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [gameType, setGameType] = useState("");
  const [filterType, setFilterType] = useState("");
  const [fromDate, setFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [printViewOpen, setPrintViewOpen] = useState(false);
  const [userId, setUserId] = useState(null);
  const [outletUsers, setOutletUsers] = useState([]);
  const [mobileUsers, setMobileUsers] = useState([]);

  const loadOutletUsers = async () => {
    try {
      const res = await txApi.getOutletUsers();
      if (res?.isSuccess) setOutletUsers(res.data || []);
      else toast.error(res?.message || "Failed to load outlet users");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load outlet users",
      );
    }
  };
  const loadMobileUsers = async () => {
    try {
      const res = await txApi.getMobileUsers();
      if (res?.isSuccess) setMobileUsers(res.data || []);
      else toast.error(res?.message || "Failed to load mobile users");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load mobile users",
      );
    }
  };

  useEffect(() => {
    if ((Number(user?.role_id) === 2 || Number(user?.role_id) === 5) || Number(user?.role_id) === 1)
      loadOutletUsers();
    if (
      (Number(user?.role_id) === 2 || Number(user?.role_id) === 5) ||
      Number(user?.role_id) === 1 ||
      Number(user?.role_id) === 3
    )
      loadMobileUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getPlaceBetList({
        search: search || undefined,
        game_type: gameType || undefined,
        filter_type: filterType || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        userId: userId || undefined,
        page,
        limit,
      });
      if (res?.isSuccess === false) {
        setList([]);
        setPagination(null);
        toast.error(res?.message || "Failed to load place bets");
      } else if (Array.isArray(res?.data)) {
        setList(res.data);
        setPagination(res?.pagination ?? null);
      } else {
        setList([]);
        setPagination(null);
      }
    } catch (err) {
      console.error("fetchList error:", err);
      toast.error(err?.response?.data?.message || "Failed to load place bets");
      setList([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [search, gameType, filterType, fromDate, toDate, userId, page, limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = async (row) => {
    setDetailModalOpen(true);
    setSelectedDetail(null);
    setDetailLoading(true);
    try {
      const res = await api.getPlaceBetById(row.id);
      if (res?.isSuccess && res?.data) {
        setSelectedDetail(res.data);
      } else {
        toast.error(res?.message || "Failed to load detail");
      }
    } catch (err) {
      console.error("getPlaceBetById error:", err);
      toast.error(err?.response?.data?.message || "Failed to load detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const openPrint = async (row) => {
    setPrintViewOpen(true);
    setSelectedDetail(null);
    setDetailLoading(true);
    try {
      const res = await api.getPlaceBetById(row.id);
      if (res?.isSuccess && res?.data) {
        setSelectedDetail(res.data);
      } else {
        toast.error(res?.message || "Failed to load detail for print");
        setPrintViewOpen(false);
      }
    } catch (err) {
      console.error("getPlaceBetById for print error:", err);
      toast.error(err?.response?.data?.message || "Failed to load detail");
      setPrintViewOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatProfitLoss = (pl) => {
    if (!pl) return "—";
    if (pl.type === "EVEN") return "0.00";
    const val = Number(pl.value) || 0;
    const str = `${val.toFixed(2)}`;
    return pl.type === "PROFIT" ? `+${str}` : `-${str}`;
  };

  const resolveBetUserId = (detail) =>
    detail?.user?.user_id ?? detail?.place_bet_data?.user_id ?? null;

  return (
    <div className="pbm-wrap">
      <div className="pbm-header">
        <div className="pbm-filters">
          <div className="pbm-filters-row">
            <div className="pbm-search-wrap">
              <CiSearch className="pbm-search-icon" size={20} />
              <input
                type="text"
                placeholder="Search by username or PlaceBet UID..."
                className="pbm-search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            {(user.role_id === 1 || (user.role_id === 2 || user.role_id === 5)) && (
              <div className="pbm-user-pick">
                <Select
                  styles={selectCustomStyles}
                  aria-labelledby="aria-label"
                  inputId="outlet-users-select"
                  instanceId="outlet-users-select"
                  name="outlet-users-select"
                  isSearchable={true}
                  openMenuOnFocus={true}
                  options={outletUsers.map((u) => ({
                    value: Number(u.id),
                    label: u.name,
                  }))}
                  value={
                    outletUsers
                      .map((u) => ({ value: Number(u.id), label: u.name }))
                      .find((o) => o.value === Number(userId)) || null
                  }
                  onChange={(opt) => {
                    setUserId(opt?.value ?? null);
                    setPage(1);
                  }}
                  placeholder="Select Outlet"
                  isClearable={true}
                  classNamePrefix="pbm-rs"
                />
              </div>
            )}
            {(user.role_id === 1 ||
              (user.role_id === 2 || user.role_id === 5) ||
              user.role_id === 3) && (
              <div className="pbm-user-pick">
                <Select
                  styles={selectCustomStyles}
                  aria-labelledby="aria-label"
                  inputId="mobile-users-select"
                  instanceId="mobile-users-select"
                  name="mobile-users-select"
                  isSearchable={true}
                  openMenuOnFocus={true}
                  options={mobileUsers.map((u) => ({
                    value: Number(u.id),
                    label: u.name,
                  }))}
                  value={
                    mobileUsers
                      .map((u) => ({ value: Number(u.id), label: u.name }))
                      .find((o) => o.value === Number(userId)) || null
                  }
                  onChange={(opt) => {
                    setUserId(opt?.value ?? null);
                    setPage(1);
                  }}
                  placeholder="Select Mobile"
                  isClearable={true}
                  classNamePrefix="pbm-rs"
                />
              </div>
            )}
          </div>
          <div className="pbm-filters-row pbm-filters-row--when">
            <select
              className="pbm-select"
              value={gameType}
              onChange={(e) => {
                setGameType(e.target.value);
                setPage(1);
              }}
            >
              {gameTypeOptions.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className="pbm-select"
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(1);
              }}
            >
              {FILTER_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="pbm-date-wrap">
              <input
                type="date"
                className="pbm-date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
              />
              <span className="pbm-date-sep">to</span>
              <input
                type="date"
                className="pbm-date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <PlaceBetDownload
              search={search}
              gameType={gameType}
              filterType={filterType}
              fromDate={fromDate}
              toDate={toDate}
              userId={userId}
            />
          </div>
        </div>
      </div>

      <div className="pbm-content">
        {loading ? (
          <Loader message="Loading place bets..." />
        ) : list.length === 0 ? (
          <NoData
            message="No place bets found"
            subText="Try adjusting filters or search."
          />
        ) : (
          <>
            <div className="pbm-table-wrap">
              <table className="pbm-table">
                <thead>
                  <tr>
                    <th>PlaceBet UID</th>
                    <th>Amount</th>
                    <th>Winning</th>
                    <th>Profit/Loss</th>
                    {Number(user?.role_id) !== 4 && <th>Scan</th>}
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => (
                    <tr
                      key={row.id}
                      className={
                        row?.role === "Mobile" && Number(row.win_amount) > 0
                          ? "pbm-row-bg-mobile-profit"
                          : row?.role === "Mobile" && Number(row.win_amount) < 0
                            ? "pbm-row-bg-mobile-loss"
                            : row?.role === "Mobile" &&
                                Number(row.win_amount) === 0
                              ? "pbm-row-bg-mobile-even"
                              : row.isDelete
                                ? "pbm-row-bg-cancelled"
                                : row.isScan
                                  ? "pbm-row-bg-scanned"
                                  : row?.role === "Mobile"
                                    ? "pbm-row-bg-mobile"
                                    : "pbm-row-bg-pending"
                      }
                    >
                      <td className="pbm-uid">
                        {row?.role === "Mobile" ? (
                          <span className="pbm-uid">
                            {stripPlaceBetUidPrefix(row.placebet_uid) || "—"} [#
                            {row.session_no}]
                          </span>
                        ) : (
                          <>
                            <div>
                              <span className="pbm-uid">
                                {stripPlaceBetUidPrefix(row.placebet_uid) ||
                                  "—"}{" "}
                                [#{row.session_no}]
                              </span>
                            </div>
                            <div className="ps-3">
                              [Bar Code: {row.bar_code}]
                            </div>
                          </>
                        )}
                      </td>
                      {/* <td>
                        <span className="pbm-user-name">
                          {row.user_name || "—"}
                        </span>
                        {row.role && (
                          <span className="pbm-role">{row.role}</span>
                        )}
                      </td>
                      <td>{row.game_type}</td>
                      <td>{row.total_bet != null ? row.total_bet : "—"}</td> */}
                      <td className="pbm-amount">
                        {Number(row.amount || 0).toFixed(2)}
                      </td>
                      <td>
                        {row.win_amount != null ? (
                          <span className="pbm-winning-amount">
                            {Number(row.win_amount).toFixed(2)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <span
                          className={
                            row?.role === "Mobile" && Number(row.win_amount) > 0
                              ? Number(row.win_amount) - Number(row.amount) > 0
                                ? "pbm-pl profit"
                                : Number(row.win_amount) - Number(row.amount) <
                                    0
                                  ? "pbm-pl loss"
                                  : "pbm-pl even"
                              : row.isDelete
                                ? "—"
                                : row.isScan &&
                                    row.profit_or_loss?.type === "PROFIT"
                                  ? "pbm-pl profit"
                                  : row.isScan &&
                                      row.profit_or_loss?.type === "LOSS"
                                    ? "pbm-pl loss"
                                    : "—"
                          }
                        >
                          {row?.role === "Mobile" && Number(row.win_amount) > 0
                            ? Number(row.win_amount) - Number(row.amount) > 0
                              ? "+" +
                                (
                                  Number(row.win_amount) - Number(row.amount)
                                ).toFixed(2)
                              : Number(row.win_amount) - Number(row.amount) < 0
                                ? (
                                    Number(row.win_amount) - Number(row.amount)
                                  ).toFixed(2)
                                : "0.00"
                            : row.isDelete
                              ? "—"
                              : row.isScan
                                ? formatProfitLoss(row.profit_or_loss)
                                : "—"}
                        </span>
                      </td>
                      {Number(user?.role_id) !== 4 && (
                        <td>
                          <span
                            className={
                              row.isDelete
                                ? "pbm-status-cancelled"
                                : Number(row.isScan) === 1
                                  ? "pbm-status-scanned"
                                  : Number(row.isScan) === 2
                                    ? "pbm-status-expired"
                                    : row?.role === "Mobile"
                                      ? "pbm-status-mobile"
                                      : "pbm-status-pending"
                            }
                          >
                            {row.isDelete
                              ? "Cancelled"
                              : Number(row.isScan) === 2
                                ? "Expired"
                                : Number(row.isScan) === 1
                                  ? "Scanned"
                                  : row?.role === "Mobile"
                                    ? "—"
                                    : "Pending"}
                          </span>
                        </td>
                      )}
                      <td>
                        {row.created_at
                          ? moment
                              .utc(row.created_at)
                              .local()
                              .format("DD/MM/YYYY hh:mm A")
                          : "—"}
                      </td>
                      <td>
                        <div className="pbm-actions">
                          <button
                            type="button"
                            className="pbm-icon-btn"
                            title="View"
                            onClick={() => openDetail(row)}
                          >
                            <MdPreview size={18} />
                          </button>
                          {/* <button
                            type="button"
                            className="pbm-icon-btn"
                            title="Print"
                            onClick={() => openPrint(row)}
                          >
                            <MdPrint size={18} />
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination && pagination.totalPages > 1 && (
              <div className="modern-pagination">
                {/* Limit Selector */}
                <div className="mp-limit">
                  <span>Show</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="500">500</option>
                    <option value="1000">1000</option>
                  </select>
                </div>

                {/* Navigation */}
                <div className="mp-controls">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Prev
                  </button>

                  <div className="mp-info">
                    Page <strong>{pagination.page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong>
                  </div>

                  <button
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>

                {/* Jump to Page */}
                <div className="mp-jump">
                  <select
                    value={page}
                    onChange={(e) => setPage(e.target.value)}
                  >
                    {Array.from({ length: pagination.totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {detailModalOpen && (
        <div
          className="pbm-modal-overlay"
          onClick={() => !detailLoading && setDetailModalOpen(false)}
        >
          <div className="pbm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pbm-modal-header">
              <h3>Place Bet Details</h3>
              <button
                type="button"
                className="pbm-modal-close"
                onClick={() => !detailLoading && setDetailModalOpen(false)}
              >
                <MdClose size={18} />
              </button>
            </div>
            <div className="pbm-modal-body">
              {detailLoading ? (
                <Loader message="Loading detail..." />
              ) : selectedDetail ? (
                <>
                  {(() => {
                    const pb = selectedDetail.place_bet_data || {};
                    const isCancelled = Number(pb.isDelete) === 1;
                    const isScanned = Number(pb.isScan) === 1;
                    const isExpired = Number(pb.isScan) === 2;
                    const userName =
                      selectedDetail.user?.user_name ||
                      selectedDetail.user?.user_username ||
                      "—";
                    const gameId = selectedDetail.user?.user_username || "—";
                    const userRole = selectedDetail.user?.role_id || "—";
                    const status = isCancelled
                      ? { label: "Cancelled", cls: "cancelled" }
                      : isScanned
                        ? { label: "Scanned", cls: "scanned" }
                        : isExpired
                          ? { label: "Expired", cls: "expired" }
                          : { label: "Pending", cls: "pending" };

                    const winningAmt = Number(
                      selectedDetail.winning_amount ?? pb.win_amount ?? 0,
                    );

                    const createdAtLabel = pb.created_at
                      ? moment
                          .utc(pb.created_at)
                          .local()
                          .format("DD/MM/YYYY hh:mm A")
                      : "—";

                    return (
                      <>
                        <div className="pbm-detail-hero">
                          <div className="pbm-detail-hero-left">
                            <div className="pbm-detail-main">
                              {userName}
                              <span className="pbm-detail-game-id">
                                [{gameId}]
                              </span>
                            </div>

                            <div className="pbm-detail-uid">
                              {userRole === 4
                                ? stripPlaceBetUidPrefix(
                                    selectedDetail.placebet_uid,
                                  )
                                : selectedDetail.placebet_uid}
                              [#{selectedDetail.session?.session_no ?? "—"}]
                            </div>
                            <div className="pbm-detail-subline">
                              <span className="pbm-detail-dot">•</span>
                              <span>
                                {selectedDetail.session?.game_type ??
                                  pb.game_type ??
                                  "—"}
                              </span>
                              <span className="pbm-detail-dot">•</span>
                              <span>{createdAtLabel}</span>
                              {pb.draw_time ? (
                                <>
                                  <span className="pbm-detail-dot">•</span>
                                  <span>
                                    Draw: {pb?.current_time}-{pb.draw_time}
                                  </span>
                                </>
                              ) : null}
                              <span className="pbm-detail-dot">•</span>
                              <span>Bet: {pb.total_bet ?? "—"}</span>
                            </div>
                            {userRole !== 4 && (
                              <div className="pbm-detail-status">
                                <span className={`pbm-chip ${status.cls}`}>
                                  {status.label}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        {!isCancelled && winningAmt !== null && (
                          <div className="pbm-summary-row">
                            <div className="pbm-summary-item">
                              <span className="pbm-detail-label">Win: </span>
                              <span className="pbm-detail-value">
                                ₹{winningAmt.toFixed(2)}
                              </span>
                            </div>
                            <div className="pbm-summary-item">
                              <span className="pbm-detail-label">
                                Profit/Loss:{" "}
                              </span>
                              <span
                                className={
                                  selectedDetail.profit_or_loss?.type ===
                                  "PROFIT"
                                    ? "pbm-stat-pl profit"
                                    : selectedDetail.profit_or_loss?.type ===
                                        "LOSS"
                                      ? "pbm-stat-pl loss"
                                      : "pbm-stat-pl even"
                                }
                              >
                                {formatProfitLoss(
                                  selectedDetail.profit_or_loss,
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <PlaceBetDetailYantraList
                    placeBetData={selectedDetail.place_bet_data}
                    winningYantraIds={selectedDetail.winning_yantra_ids}
                    betUserId={resolveBetUserId(selectedDetail)}
                    enabled={detailModalOpen}
                  />
                </>
              ) : (
                <NoData message="No detail available" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Print View */}
      {printViewOpen && selectedDetail && (
        <div className="pbm-print-wrap">
          <div className="pbm-print-view">
            <div className="pbm-print-actions no-print">
              <button
                type="button"
                className="pbm-print-btn"
                onClick={handlePrint}
              >
                Print
              </button>
              <button
                type="button"
                className="pbm-print-close"
                onClick={() => {
                  setPrintViewOpen(false);
                  setSelectedDetail(null);
                }}
              >
                Close
              </button>
            </div>
            <div className="pbm-print-body">
              <h2 className="pbm-print-title">Place Bet Details</h2>
              <div className="pbm-print-qr">
                {selectedDetail.placebet_uid && (
                  <>
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(
                        `${window.location.origin}/view-bet/${encodeURIComponent(selectedDetail.placebet_uid)}`,
                      )}`}
                      alt="Bet QR"
                    />
                    <div className="pbm-print-uid">
                      {selectedDetail.placebet_uid}
                    </div>
                  </>
                )}
              </div>
              <div className="pbm-print-details">
                <div className="pbm-print-row">
                  <span>User</span>
                  <span>
                    {selectedDetail.user?.user_name ||
                      selectedDetail.user?.user_username ||
                      "—"}
                  </span>
                </div>
                <div className="pbm-print-row">
                  <span>Session</span>
                  <span>{selectedDetail.place_bet_data?.game_type || "—"}</span>
                </div>
                <div className="pbm-print-row">
                  <span>Total Bet</span>
                  <span>{selectedDetail.place_bet_data?.total_bet ?? "—"}</span>
                </div>
                <div className="pbm-print-row">
                  <span>Amount</span>
                  <span>
                    ₹
                    {Number(selectedDetail.place_bet_data?.amount || 0).toFixed(
                      2,
                    )}
                  </span>
                </div>
                <div className="pbm-print-row">
                  <span>Winning Amount</span>
                  <span>
                    ₹{Number(selectedDetail.winning_amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="pbm-print-row">
                  <span>Profit/Loss</span>
                  <span
                    className={
                      selectedDetail.profit_or_loss?.type === "PROFIT"
                        ? "profit"
                        : selectedDetail.profit_or_loss?.type === "LOSS"
                          ? "loss"
                          : ""
                    }
                  >
                    {formatProfitLoss(selectedDetail.profit_or_loss)}
                  </span>
                </div>
                <div className="pbm-print-row">
                  <span>Created</span>
                  <span>
                    {selectedDetail.place_bet_data?.created_at
                      ? moment
                          .utc(selectedDetail.place_bet_data.created_at)
                          .local()
                          .format("DD-MM-YYYY HH:mm")
                      : "—"}
                  </span>
                </div>
              </div>
              <PlaceBetPrintYantraGrid
                placeBetData={selectedDetail.place_bet_data}
                betUserId={resolveBetUserId(selectedDetail)}
                enabled={printViewOpen}
              />
            </div>
          </div>
          <div
            className="pbm-print-overlay no-print"
            onClick={() => {
              setPrintViewOpen(false);
              setSelectedDetail(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PlaceBetManagement;
